import { useEffect, useMemo, useState } from 'react'
import MFAccordion from '../../ui-components/MFAccordion'
import { MUTUAL_FUND_DETAIL_IMAGE_URL } from '../../../constants'
import Tabs from '../../ui-components/Tabs'
import { TextToggle } from '../../ui-components/TextToggle'
import DistributionViz from './DistributionViz'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { convertUnitAndValue, toPascalCaseWithSpaces } from '../../../utils/utils'
import { DistributionDatum } from '../../../types/mutual-fund-detail.types'
import { EventNames, UNITS } from '../../../types/misc.types'
import { motion, AnimatePresence } from 'framer-motion'
import { trackEvent } from '../../../utils/appBridge'
import { GROUPED_STATIC_TEXT_ROLE } from '../../ui-components/NameValueTableRow'

const OTHERS_NOTE =
  'Others include assets like commodities, real estate, derivatives, payout-linked securities and other non-traditional investments.'

export default function DistributionSection() {
  const { data, isin } = useMetaDataContext()
  const [isExpanded, setIsExpanded] = useState(false)
  const [tabsData, setTabsData] = useState<any>([])
  const [selectedTabId, setSelectedTabId] = useState<string>('')
  const [toggles, setToggles] = useState<string[]>([])
  const [toggleMap, setToggleMap] = useState<{ [label: string]: 'MARKET_CAP' | 'SECTORS' }>({})
  const [selectedToggle, setSelectedToggle] = useState<string>('')
  const [currentDistributionData, setCurrentDistributionData] = useState<DistributionDatum[]>()

  const buildTabsData = () => {
    return data?.distribution
      ?.filter((datum) => datum?.allocationPercentage !== 0)
      ?.map((datum) => {
        return {
          id: datum?.nature,
          label: toPascalCaseWithSpaces(datum?.nature ?? ''),
          labelComponent: (
            <DistributionTabLabel nature={datum?.nature} allocationPercentage={datum?.allocationPercentage} />
          )
        }
      })
  }

  const getTabData = () => {
    return data?.distribution?.find((distributionDatum) => distributionDatum?.nature === selectedTabId)
  }

  useEffect(() => {
    setCurrentDistributionData(
      () =>
        getTabData()?.distributionTypeToHoldings?.[toggleMap?.[selectedToggle]]?.sort(
          (a, b) => b?.weightage - a?.weightage
        ) as DistributionDatum[]
    )
  }, [selectedTabId, selectedToggle])

  useEffect(() => {
    const tabData = getTabData()
    if (tabData) {
      const currentToggles = tabData?.types
      const modifiedToggles = currentToggles?.map((typeDatum) => toPascalCaseWithSpaces(typeDatum))
      setToggles(() => modifiedToggles)
      setSelectedToggle((togglePrev) => {
        return modifiedToggles?.indexOf(togglePrev) === -1 ? modifiedToggles?.[0] : togglePrev
      })
      const toggleMapCopy: { [label: string]: 'MARKET_CAP' | 'SECTORS' } = {}
      currentToggles?.forEach((toggle) => {
        const pascalToggle = toPascalCaseWithSpaces(toggle) as 'MARKET_CAP' | 'SECTORS'
        toggleMapCopy[pascalToggle] = toggle as 'MARKET_CAP' | 'SECTORS'
      })
      setToggleMap(() => toggleMapCopy)
    }
  }, [selectedTabId])

  useEffect(() => {
    if (data) {
      const tabsDataCopy = buildTabsData()
      setTabsData(() => tabsDataCopy)
      setSelectedTabId(() => tabsDataCopy?.[0]?.id as string)
    }
  }, [data])

  const areTogglesUnavailable = useMemo(() => {
    return toggles?.length <= 0
  }, [toggles])

  return (
    <MFAccordion
      icon={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/distribution-icon.svg`}
      title='Asset Allocation'
      isExpanded={isExpanded}
      onAccordionClick={() =>
        setIsExpanded((isExpandedPrev) => {
          trackEvent({
            eventName: EventNames.UserClicked,
            payload: {
              eventId: 'Fund_Distribution',
              responseName: isExpandedPrev ? 'Collapse' : 'Expand',
              screenType: 'MF_L2',
              fundName: isin
            }
          })
          return !isExpandedPrev
        })
      }
      isBorderTop={true}
    >
      <div className='flex flex-col gap-[26px] pb-[16px]'>
        <div className='w-full h-[58px]'>
          <Tabs
            selectedTabId={selectedTabId}
            tabsData={tabsData}
            onTabClick={(tabId: any) => {
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  eventId: 'Fund_Distribution',
                  responseName: `${tabId}`,
                  fundName: isin,
                  screenType: 'MF_L2'
                }
              })
              setSelectedTabId(tabId)
            }}
            isFullWidth
          />
        </div>

        <AnimatePresence mode='wait'>
          <motion.div
            layout
            key={selectedTabId}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className='overflow-hidden flex flex-col gap-4 text-[14px]'
          >
            <div className='flex justify-between'>
              <DistributionStatusLabel areTogglesUnavailable={areTogglesUnavailable} />
              {!areTogglesUnavailable && (
                <div className='font-[500]'>
                  <TextToggle
                    currentToggleOption={selectedToggle}
                    toggleOptions={toggles}
                    onToggleClick={(toggleOption: any) => setSelectedToggle(() => toggleOption)}
                    toggleColor='#3E6CE3'
                  />
                </div>
              )}
            </div>
            <div>
              {areTogglesUnavailable ? (
                <div className='flex flex-col'>
                  <hr className='mb-5 mt-1 bg-[#EBF2F2]' />
                  {selectedTabId === 'OTHERS' ? <DistributionOthersNote /> : null}
                </div>
              ) : (
                <DistributionViz distributionData={currentDistributionData ?? []} />
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </MFAccordion>
  )
}

export function DistributionTabLabel({
  nature,
  allocationPercentage
}: {
  nature?: string
  allocationPercentage?: number
}) {
  const natureLabel = toPascalCaseWithSpaces(nature ?? '')
  const showPercentage = (allocationPercentage ?? -1) > -1
  const percentageLabel = showPercentage
    ? convertUnitAndValue({
        value: allocationPercentage ?? 0,
        unit: UNITS.PERCENTAGE
      })
    : ''
  const ariaLabel = showPercentage ? `${natureLabel}, ${percentageLabel}` : natureLabel

  return (
    <div
      role={GROUPED_STATIC_TEXT_ROLE}
      aria-label={ariaLabel}
      className='flex flex-col justify-center py-[6px] leading-[150%] min-h-[50px]'
    >
      <div aria-hidden='true'>
        <div className='font-[400]'>{natureLabel}</div>
        {showPercentage ? <div className='font-[700] text-[14px]'>{percentageLabel}</div> : null}
      </div>
    </div>
  )
}

export function DistributionStatusLabel({ areTogglesUnavailable }: { areTogglesUnavailable: boolean }) {
  const label = areTogglesUnavailable ? 'Fund Distribution Not Available' : 'Distribution'

  return (
    <div role={GROUPED_STATIC_TEXT_ROLE} aria-label={label} className='font-[500]'>
      <span aria-hidden='true'>{label}</span>
    </div>
  )
}

export function DistributionOthersNote() {
  return (
    <div role={GROUPED_STATIC_TEXT_ROLE} aria-label={OTHERS_NOTE} className='text-[#8E9393] text-13 w-11/12'>
      <span aria-hidden='true'>{OTHERS_NOTE}</span>
    </div>
  )
}
